import {
  DATE_SPLIT_SYMBOL,
  DatatypeOperatorType,
  Direction,
  DraftDatatypeComparisonOperator,
  DraftDatatypeOperator,
  DraftFilter,
  DraftObjectOperator,
  OPERATOR_SYMBOLS_BY_DATATYPE_OPERATOR,
  ObjectOperatorType,
  OperatorType,
  Variable,
} from 'vet-bones/utils/queryBuilder';

import {
  GEOSPARQL_JSON_LITERAL_IRI,
  GEOSPARQL_WKT_LITERAL_IRI,
  RDFS_LABEL_IRI,
  STARDOG_LABEL_IRI,
  XSD_BOOLEAN_IRI,
  XSD_DATE_IRI,
  XSD_DATE_TIME_IRI,
  XSD_DAY_TIME_DURATION_IRI,
  XSD_DECIMAL_IRI,
  XSD_DOUBLE_IRI,
  XSD_DURATION_IRI,
  XSD_FLOAT_IRI,
  XSD_INTEGER_IRI,
  XSD_STRING_IRI,
  XSD_TIME_IRI,
  XSD_YEAR_MONTH_DURATION_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import {
  DATA_VARIABLE,
  OBJECT_VARIABLE,
} from 'src/utils/stringify/rules/constants';

export type FilterQueryParts = {
  triple: string;
  typeVariable?: {
    id: string;
    instanceIri: string;
    typeIri: string;
    variable: string;
  };
};

export const buildIfObjectFilter = ({
  objectIri,
  objectVariable,
  operator,
  subjectIri,
  subjectVariable,
  value,
}: {
  objectIri: string;
  objectVariable: string;
  operator: DraftObjectOperator;
  subjectIri: string;
  subjectVariable: string;
  value: Variable;
}): FilterQueryParts | null => {
  const { direction, iri, type } = operator;
  const isOutbound = direction === Direction.OUTBOUND;

  const subject = subjectIri ? `<${subjectIri}>` : subjectVariable;
  const object = objectIri ? `<${objectIri}>` : objectVariable;

  switch (type) {
    case ObjectOperatorType.CONNECTED: {
      const triple = isOutbound
        ? `${subject} <${iri}> ${object}`
        : `${object} <${iri}> ${subject}`;
      return {
        triple: `${triple} .`,
        typeVariable: {
          id: value.id,
          instanceIri: objectIri,
          typeIri: value.iri,
          variable: objectVariable,
        },
      };
    }

    default:
      return null;
  }
};

export const buildIfDatatypeFilter = ({
  objectVariable,
  operator,
  subjectIri,
  subjectVariable,
  value,
}: {
  objectVariable: string;
  operator: DraftDatatypeOperator;
  subjectIri: string;
  subjectVariable: string;
  value: string;
}): FilterQueryParts | null => {
  const { iri, range, type } = operator;

  const subject = subjectIri ? `<${subjectIri}>` : subjectVariable;
  const predicateIri = iri === STARDOG_LABEL_IRI ? RDFS_LABEL_IRI : iri;
  const triple = `${subject} <${predicateIri}> ${objectVariable}`;

  if (type === DatatypeOperatorType.EXISTS) {
    return { triple: `${triple} .` };
  }

  if (type === DatatypeOperatorType.DOES_NOT_EXIST) {
    return {
      triple: `OPTIONAL { ${triple} . } . FILTER(!BOUND(${objectVariable})) .`,
    };
  }

  const rangeIri = range || XSD_STRING_IRI;

  let filterStatement = '';
  switch (range) {
    case null:
    case XSD_STRING_IRI: {
      // always cast to string, even when range is unknown, to ensure that
      // things like "foo" = "foo"@en work correctly
      const variable = `STR(${objectVariable})`;
      if (
        type === DatatypeOperatorType.EQUALS ||
        type === DatatypeOperatorType.DOES_NOT_EQUAL
      ) {
        const sign = OPERATOR_SYMBOLS_BY_DATATYPE_OPERATOR[type];
        filterStatement = `FILTER(${variable} ${sign} "${escapeDoubleQuotes(
          value
        )}")`;
      } else if (type === DatatypeOperatorType.CONTAINS) {
        filterStatement = `FILTER(CONTAINS(${variable}, "${escapeDoubleQuotes(
          value
        )}"))`;
      } else if (type === DatatypeOperatorType.DOES_NOT_CONTAIN) {
        filterStatement = `FILTER(!CONTAINS(${variable}, "${escapeDoubleQuotes(
          value
        )}"))`;
      }
      break;
    }

    case GEOSPARQL_JSON_LITERAL_IRI:
    case GEOSPARQL_WKT_LITERAL_IRI: {
      if (
        type === DatatypeOperatorType.EQUALS ||
        type === DatatypeOperatorType.DOES_NOT_EQUAL
      ) {
        const sign = OPERATOR_SYMBOLS_BY_DATATYPE_OPERATOR[type];
        filterStatement = `FILTER(${objectVariable} ${sign} "${escapeDoubleQuotes(
          value
        )}"^^<${rangeIri}>)`;
      }
      break;
    }

    case XSD_BOOLEAN_IRI: {
      if (type === DatatypeOperatorType.IS) {
        filterStatement = `FILTER(!${objectVariable} = !${value})`;
      }
      break;
    }

    case XSD_DAY_TIME_DURATION_IRI:
    case XSD_DECIMAL_IRI:
    case XSD_DOUBLE_IRI:
    case XSD_DURATION_IRI:
    case XSD_FLOAT_IRI:
    case XSD_INTEGER_IRI:
    case XSD_YEAR_MONTH_DURATION_IRI: {
      if (
        type === DatatypeOperatorType.EQUALS ||
        type === DatatypeOperatorType.DOES_NOT_EQUAL ||
        type === DatatypeOperatorType.GT ||
        type === DatatypeOperatorType.LT ||
        type === DatatypeOperatorType.GTE ||
        type === DatatypeOperatorType.LTE
      ) {
        const sign = OPERATOR_SYMBOLS_BY_DATATYPE_OPERATOR[type];
        filterStatement = `FILTER(${objectVariable} ${sign} "${escapeDoubleQuotes(
          value
        )}"^^<${rangeIri}>)`;
      }
      break;
    }

    case XSD_DATE_TIME_IRI:
    case XSD_DATE_IRI:
    case XSD_TIME_IRI: {
      if (
        type === DatatypeOperatorType.ON ||
        type === DatatypeOperatorType.NOT_ON ||
        type === DatatypeOperatorType.BEFORE ||
        type === DatatypeOperatorType.AFTER ||
        type === DatatypeOperatorType.BEFORE_OR_ON ||
        type === DatatypeOperatorType.AFTER_OR_ON
      ) {
        const sign = OPERATOR_SYMBOLS_BY_DATATYPE_OPERATOR[type];
        filterStatement = `FILTER(${objectVariable} ${sign} "${escapeDoubleQuotes(
          value
        )}"^^<${rangeIri}>)`;
      } else if (type === DatatypeOperatorType.BETWEEN) {
        const [min, max] = value.split(DATE_SPLIT_SYMBOL);
        filterStatement = `FILTER(${objectVariable} >= "${escapeDoubleQuotes(
          min
        )}"^^<${rangeIri}> && ${objectVariable} <= "${escapeDoubleQuotes(
          max
        )}"^^<${rangeIri}>)`;
      }
      break;
    }

    default:
      break;
  }

  if (!filterStatement) {
    return null;
  }

  return { triple: `${triple} . ${filterStatement} .` };
};

export const buildIfDatatypeComparisonFilter = ({
  comparisonObjectVariable,
  comparisonOperator,
  comparisonSubjectIri,
  comparisonSubjectVariable,
  comparisonVariable,
  objectVariable,
  operator,
  subjectIri,
  subjectVariable,
}: {
  comparisonObjectVariable: string;
  comparisonOperator: DraftDatatypeOperator;
  comparisonSubjectIri: string;
  comparisonSubjectVariable: string;
  comparisonVariable: Variable;
  objectVariable: string;
  operator: DraftDatatypeComparisonOperator;
  subjectIri: string;
  subjectVariable: string;
}): FilterQueryParts | null => {
  const { iri, range, type } = operator;

  let filterStatement = '';

  const subject = subjectIri ? `<${subjectIri}>` : subjectVariable;
  const predicateIri = iri === STARDOG_LABEL_IRI ? RDFS_LABEL_IRI : iri;
  const subjectTriple = `${subject} <${predicateIri}> ${objectVariable}`;

  const comparisonSubject = comparisonSubjectIri
    ? `<${comparisonSubjectIri}>`
    : comparisonSubjectVariable;
  const comparisonPredicateIri =
    comparisonOperator.iri === STARDOG_LABEL_IRI
      ? RDFS_LABEL_IRI
      : comparisonOperator.iri;
  const comparisonSubjectTriple = `${comparisonSubject} <${comparisonPredicateIri}> ${comparisonObjectVariable}`;

  switch (range) {
    case null:
    case XSD_STRING_IRI: {
      // always cast to string, even when range is unknown, to ensure that
      // things like "foo" = "foo"@en work correctly
      const stringVariable = `STR(${objectVariable})`;
      const stringComparisonVariable = `STR(${comparisonObjectVariable})`;

      if (
        type === DatatypeOperatorType.EQUALS ||
        type === DatatypeOperatorType.DOES_NOT_EQUAL
      ) {
        const sign = OPERATOR_SYMBOLS_BY_DATATYPE_OPERATOR[type];
        filterStatement = `FILTER(${stringVariable} ${sign} ${stringComparisonVariable})`;
      } else if (type === DatatypeOperatorType.CONTAINS) {
        filterStatement = `FILTER(CONTAINS(${stringVariable}, ${stringComparisonVariable}))`;
      } else if (type === DatatypeOperatorType.DOES_NOT_CONTAIN) {
        filterStatement = `FILTER(!CONTAINS(${stringVariable}, ${stringComparisonVariable}))`;
      }
      break;
    }

    case GEOSPARQL_JSON_LITERAL_IRI:
    case GEOSPARQL_WKT_LITERAL_IRI:
    case XSD_BOOLEAN_IRI: {
      if (
        type === DatatypeOperatorType.EQUALS ||
        type === DatatypeOperatorType.DOES_NOT_EQUAL
      ) {
        const sign = OPERATOR_SYMBOLS_BY_DATATYPE_OPERATOR[type];
        filterStatement = `FILTER(${objectVariable} ${sign} ${comparisonObjectVariable})`;
      }
      break;
    }

    case XSD_DAY_TIME_DURATION_IRI:
    case XSD_DECIMAL_IRI:
    case XSD_DOUBLE_IRI:
    case XSD_DURATION_IRI:
    case XSD_FLOAT_IRI:
    case XSD_INTEGER_IRI:
    case XSD_YEAR_MONTH_DURATION_IRI: {
      if (
        type === DatatypeOperatorType.EQUALS ||
        type === DatatypeOperatorType.DOES_NOT_EQUAL ||
        type === DatatypeOperatorType.GT ||
        type === DatatypeOperatorType.LT ||
        type === DatatypeOperatorType.GTE ||
        type === DatatypeOperatorType.LTE
      ) {
        const sign = OPERATOR_SYMBOLS_BY_DATATYPE_OPERATOR[type];
        filterStatement = `FILTER(${objectVariable} ${sign} ${comparisonObjectVariable})`;
      }
      break;
    }

    case XSD_DATE_TIME_IRI:
    case XSD_DATE_IRI:
    case XSD_TIME_IRI: {
      if (
        type === DatatypeOperatorType.ON ||
        type === DatatypeOperatorType.NOT_ON ||
        type === DatatypeOperatorType.BEFORE ||
        type === DatatypeOperatorType.AFTER ||
        type === DatatypeOperatorType.BEFORE_OR_ON ||
        type === DatatypeOperatorType.AFTER_OR_ON
      ) {
        const sign = OPERATOR_SYMBOLS_BY_DATATYPE_OPERATOR[type];
        filterStatement = `FILTER(${objectVariable} ${sign} ${comparisonObjectVariable})`;
      }
      break;
    }

    default:
      break;
  }

  if (!filterStatement) {
    return null;
  }

  return {
    triple: `${subjectTriple} . ${comparisonSubjectTriple} . ${filterStatement} .`,
    typeVariable: {
      id: comparisonVariable.id,
      instanceIri: comparisonSubjectIri,
      typeIri: comparisonVariable.iri,
      variable: comparisonSubjectVariable,
    },
  };
};

export const buildIfFilter = ({
  getInstanceIri,
  getVariable,
  filter,
}: {
  getInstanceIri: (variable: Variable) => string;
  getVariable: (variable: string, id?: string) => string;
  filter: DraftFilter;
}): FilterQueryParts | null => {
  const { subject, operator, value, comparisonSubject, comparisonOperator } =
    filter;

  if (!subject || !operator) {
    console.debug('no subject or operator for filter:', filter);
    return null;
  }

  const subjectIri = getInstanceIri(subject) || '';
  const subjectVariable = getVariable(OBJECT_VARIABLE, subject.id);

  if (
    operator.operatorType === OperatorType.DATATYPE &&
    operator.comparison &&
    comparisonSubject &&
    comparisonOperator
  ) {
    const objectVariable = getVariable(DATA_VARIABLE);
    const comparisonObjectVariable = getVariable(DATA_VARIABLE);
    const comparisonSubjectIri = getInstanceIri(comparisonSubject) || '';
    const comparisonSubjectVariable = getVariable(
      OBJECT_VARIABLE,
      comparisonSubject.id
    );
    return buildIfDatatypeComparisonFilter({
      comparisonObjectVariable,
      comparisonOperator,
      comparisonSubjectIri,
      comparisonSubjectVariable,
      comparisonVariable: comparisonSubject,
      objectVariable,
      operator,
      subjectIri,
      subjectVariable,
    });
  }

  if (
    operator.operatorType === OperatorType.DATATYPE &&
    !operator.comparison &&
    (typeof value === 'string' || value === undefined || value === null)
  ) {
    const objectVariable = getVariable(DATA_VARIABLE);
    return buildIfDatatypeFilter({
      objectVariable,
      operator,
      subjectIri,
      subjectVariable,
      value: value || '',
    });
  }

  if (operator.operatorType === OperatorType.OBJECT && value) {
    const objectValue = value as Variable;
    const objectIri = getInstanceIri(objectValue) || '';
    const objectVariable = getVariable(OBJECT_VARIABLE, objectValue.id);

    return buildIfObjectFilter({
      objectIri,
      objectVariable,
      operator,
      subjectIri,
      subjectVariable,
      value: objectValue,
    });
  }

  return null;
};
